import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task, waitForProperty } from 'ember-concurrency';
import type HotelSettingsBudgetDetailController from 'lumen/controllers/hotel/settings/budget/detail';
import type { Transition } from 'lumen/services/transition';
import {
  BudgetForecastDataFetching,
  isErrorWithStatusCode,
} from 'lumen/utils/budget-forecast/data-fetching';
import { LayoutSettingsHandler } from 'lumen/utils/budget-forecast/layout-settings';
import {
  ADDITIONAL_DATA_TYPES,
  type DateGranularity,
} from 'lumen/utils/constants/budget-forecast-constants';
import { DRAWER_IDS } from 'lumen/utils/constants/general-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import {
  setupUnsavedChangesTransitionHandler,
  type TransitionHandler,
} from 'lumen/utils/unsaved-changes-transition-handlers';

type BudgetDetailParams = {
  endDate: string;
  entry_id: string;
  granularity: DateGranularity;
  startDate: string;
  versionId: string | undefined;
};

export type BudgetDetailRouteModel = {
  capacities: typeof BudgetForecastDataFetching.prototype.requestCapacitiesTask;
  dataItems: typeof BudgetForecastDataFetching.prototype.requestDataItemsTask;
  entries: typeof BudgetForecastDataFetching.prototype.fetchEntriesTask;
  entry: typeof BudgetForecastDataFetching.prototype.fetchEntryByIdTask;
  eventsAndHolidays: typeof BudgetForecastDataFetching.prototype.requestEventsAndHolidaysTask;
  segments: typeof BudgetForecastDataFetching.prototype.requestSegmentsTask;
  versions: typeof BudgetForecastDataFetching.prototype.requestVersionsForEntryTask;
};

@protectedRoute({
  features: 'business_intelligence.settings.budget_and_forecast',
  redirectTo: 'hotel.settings',
})
export default class HotelSettingsBudgetDetailRoute extends Route {
  queryParams = {
    granularity: { refreshModel: true },
    startDate: { refreshModel: true },
    endDate: { refreshModel: true },
    versionId: { refreshModel: true },
  };

  @service declare budgetForecast: Services['budget-forecast'];
  @service declare drawer: Services['drawer'];
  @service declare router: Services['router'];
  @service declare transition: Services['transition'];

  #dataFetching: BudgetForecastDataFetching;
  #layoutSettingsHandler: LayoutSettingsHandler;
  #transitionHandler?: TransitionHandler;

  requestAndSetBudgetForecastTask = task(
    { restartable: true },
    async (params: BudgetDetailParams) => {
      const result = await this.#dataFetching.requestDataItemsTask.perform({
        ...params,
        budgetEntryId: params.entry_id,
        versionId: params.versionId,
      });

      this.budgetForecast.setAndRollbackBudgetForecastItems(result.dataItems);

      return result;
    },
  );

  constructor(owner: Owner) {
    super(owner);
    this.#dataFetching = new BudgetForecastDataFetching(owner);
    this.#layoutSettingsHandler = new LayoutSettingsHandler(owner);
  }

  beforeModel(transition: Transition): void {
    this.transition.validateBudgetForecastEntryIdRouteParam(transition);
  }

  deactivate(): void {
    this.#dataFetching.cancelAll();
    this.#dataFetching.unloadAll();
    void this.requestAndSetBudgetForecastTask.cancelAll();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: BudgetDetailParams) {
    const model: BudgetDetailRouteModel = {
      entry: this.#dataFetching.fetchEntryByIdTask,
      entries: this.#dataFetching.fetchEntriesTask,
      segments: this.#dataFetching.requestSegmentsTask,
      dataItems: this.requestAndSetBudgetForecastTask,
      eventsAndHolidays: this.#dataFetching.requestEventsAndHolidaysTask,
      capacities: this.#dataFetching.requestCapacitiesTask,
      versions: this.#dataFetching.requestVersionsForEntryTask,
    };

    this.performModelTasks(model, params);

    return model;
  }

  performModelTasks(
    model: BudgetDetailRouteModel,
    params: BudgetDetailParams,
  ): void {
    // We use .then() instead of async/await here because of a difference in event loop/microtask behavior.
    // With async/await, a rendering cycle happens between the completion of the model.entry task
    // and the start of the other tasks.
    // This leads to an issue because the loading state depends on the isRunning state of both the
    // model.entry task and (one of) the other tasks.
    model.entry
      .perform(params.entry_id)
      .then(() => {
        void model.entries.perform('budget');
        void model.segments.perform({ entryId: params.entry_id });
        void model.dataItems.perform(params);
        void model.eventsAndHolidays.perform(params);
        void model.capacities.perform(params);

        type ModelVersionsArgs = Parameters<typeof model.versions.perform>;
        const hasCachedVersionsForEntry =
          model.versions.lastSuccessful &&
          // @ts-expect-error: ember-concurrency types don't expose args
          (model.versions.lastSuccessful.args as ModelVersionsArgs)[0] ===
            params.entry_id;

        if (!hasCachedVersionsForEntry) {
          void model.versions.perform(params.entry_id);
        }
      })
      .catch((error: unknown) => {
        if (!isErrorWithStatusCode(error) || error.status !== 404) {
          // Handled by data-fetching utility class
          return;
        }

        this.router.transitionTo('hotel.settings.budget');
        return;
      });
  }

  resetController(
    controller: HotelSettingsBudgetDetailController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (!isExiting) {
      return;
    }

    // Reset selected version when navigating away from the route
    controller.versionId = undefined;
    this.drawer.collapseDrawer(DRAWER_IDS.BUDGET_FORECAST_VERSION_HISTORY);

    if (this.#transitionHandler) {
      this.#transitionHandler.cleanup();
      this.#transitionHandler = undefined;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async setupController(
    controller: HotelSettingsBudgetDetailController,
    model: BudgetDetailRouteModel,
    transition: Transition,
  ): Promise<void> {
    super.setupController(controller, model, transition);

    this.#transitionHandler = setupUnsavedChangesTransitionHandler(
      this.router,
      controller,
      'hotel.settings.budget.detail',
    );

    await waitForProperty(model.entry, 'isRunning', false);
    await waitForProperty(model.dataItems, 'isRunning', false);

    const data = model.dataItems.last?.value?.dataItems ?? [];
    const availableDataTypes = [
      ...Array.from(new Set(data.map((item) => item.dataType))).filter(
        (dataType) => dataType !== 'budget',
      ),
      ...ADDITIONAL_DATA_TYPES,
    ];

    controller.layoutSettings = this.#layoutSettingsHandler.getLayoutSettings({
      view: 'budget',
      mode: 'edit',
      availableDataTypes,
    });
  }

  @action refreshRoute(): void {
    this.refresh();
  }
}
